import React from 'react'
import { toast } from 'react-toastify'

import moment from 'moment'

import { Badge, Button, IconButton, Tooltip, useTheme } from '@material-ui/core'
import { ToggleButtonGroup } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import TuneIcon from '@material-ui/icons/Tune'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import {
    AccountTree,
    AddCircleOutline,
    AssignmentIndOutlined,
    BarChart,
} from '@material-ui/icons'

import {
    ALL,
    CONTRACTOR,
    CUSTOM,
    IN_HOUSE,
    JbFormState,
    MY_TEAM,
    NEXT_WEEK,
    THIS_WEEK,
} from '../types'
import { Container, Selector } from '../../../components'
import { User, WorkSpaceUser, ModelMap } from '../../../models'
import { ReactComponent as EZLogo } from '../../../assets/Logo.svg'
import { JobBoardSelectionMode } from '../JobBoardComponents/JobBoardSelectionMode'
import { BulkDeleteWorkOrderRequest, WorkorderResponse } from '../../../store'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    dateFilter: number
    startDate: MaterialUiPickersDate
    endDate: MaterialUiPickersDate
    vendorFilter: number
    formState: JbFormState
    failedServiceRequests: number
    ezNowCount: number
    workspaceUser?: WorkSpaceUser
    unassignedWoCount: number
    userList: User[]
    selectedUserIds: number[]
    setDateFilter: React.Dispatch<React.SetStateAction<number>>
    setStartDate: React.Dispatch<React.SetStateAction<MaterialUiPickersDate>>
    setEndDate: React.Dispatch<React.SetStateAction<MaterialUiPickersDate>>
    setVendorFilter: React.Dispatch<React.SetStateAction<number>>
    setSelectedUserIds: React.Dispatch<React.SetStateAction<number[]>>
    refreshData: (
        startDate: Date | MaterialUiPickersDate,
        endDate: Date | MaterialUiPickersDate,
    ) => void
    openFilterDrawer: () => void
    setFormState: React.Dispatch<React.SetStateAction<JbFormState>>
    isMultiselectMode: boolean
    selectedWorkorders: ModelMap<WorkorderResponse>
    setIsMultiselectMode: (isMultiselectMode: boolean) => void
    setSelectedWorkorders: (
        selectedWorkorders: ModelMap<WorkorderResponse>,
    ) => void
    bulkDeleteWorkorders: (request: BulkDeleteWorkOrderRequest) => Promise<void>
    openBulkEditModal: () => void
    openTransitionModal: () => void
    canEdit?: boolean
}

export const JobBoardHeader = (props: Props) => {
    const {
        dateFilter,
        startDate,
        endDate,
        vendorFilter,
        formState,
        failedServiceRequests,
        ezNowCount,
        unassignedWoCount,
        userList,
        selectedUserIds,
        setDateFilter,
        setStartDate,
        setEndDate,
        setVendorFilter,
        setSelectedUserIds,
        refreshData,
        openFilterDrawer,
        setFormState,
        isMultiselectMode,
        selectedWorkorders,
        setIsMultiselectMode,
        setSelectedWorkorders,
        bulkDeleteWorkorders,
        openBulkEditModal,
        openTransitionModal,
        canEdit,
    } = props

    const theme = useTheme()

    const ButtonGroupStyle: React.CSSProperties = {
        textTransform: 'none',
        fontSize: 15,
        fontWeight: 600,
    }

    return (
        <Container
            style={{
                display: 'flex',
                gap: theme.spacing(3),
                width: '100%',
                paddingTop: theme.spacing(1),
            }}
        >
            {/* Left Section - Fixed Width */}
            <Container
                style={{
                    width: 300,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                }}
            >
                <Container
                    style={{ flexDirection: 'column', gap: theme.spacing(0.5) }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h4),
                            fontWeight: 700,
                        }}
                    >
                        Job Board
                    </span>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            color: theme.palette.primary.dark,
                        }}
                    >
                        View and manage your vendors work!
                    </span>
                </Container>
                <JobBoardSelectionMode
                    isMultiselectMode={isMultiselectMode}
                    selectedWorkorders={selectedWorkorders}
                    setIsMultiselectMode={setIsMultiselectMode}
                    setSelectedWorkorders={setSelectedWorkorders}
                    bulkDeleteWorkorders={bulkDeleteWorkorders}
                    openBulkEditModal={openBulkEditModal}
                    openTransitionModal={openTransitionModal}
                    canEdit={canEdit}
                />
            </Container>

            {/* Right Section - Flexible Width */}
            <Container
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                }}
            >
                <Container
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: theme.spacing(2),
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Date Filter Section */}
                    <Container
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                            flexWrap: 'wrap',
                        }}
                    >
                        <ToggleButtonGroup
                            value={dateFilter}
                            exclusive
                            style={{
                                border: `1px solid ${theme.palette.primary.dark}`,
                                borderRadius: '5px',
                                height: 40,
                                minWidth: 'fit-content',
                            }}
                        >
                            <Button
                                value={THIS_WEEK}
                                aria-label="This Week"
                                style={{
                                    ...ButtonGroupStyle,
                                    backgroundColor:
                                        dateFilter === THIS_WEEK
                                            ? theme.palette.primary.dark
                                            : '',
                                    color:
                                        dateFilter === THIS_WEEK ? 'white' : '',
                                }}
                                onClick={() => {
                                    setDateFilter(THIS_WEEK)
                                    const tempStartDate = moment()
                                        .day(1)
                                        .toDate()
                                    const tempEndDate = moment().day(7).toDate()
                                    setStartDate(tempStartDate)
                                    setEndDate(tempEndDate)
                                    refreshData(tempStartDate, tempEndDate)
                                }}
                            >
                                This Week
                            </Button>
                            <Button
                                value={NEXT_WEEK}
                                aria-label="Next Week"
                                style={{
                                    ...ButtonGroupStyle,
                                    borderRight: `1px solid ${theme.palette.primary.dark}`,
                                    borderLeft: `1px solid ${theme.palette.primary.dark}`,
                                    backgroundColor:
                                        dateFilter === NEXT_WEEK
                                            ? theme.palette.primary.dark
                                            : '',
                                    color:
                                        dateFilter === NEXT_WEEK ? 'white' : '',
                                }}
                                onClick={() => {
                                    setDateFilter(NEXT_WEEK)
                                    const tempStartDate = moment()
                                        .day(8)
                                        .toDate()
                                    const tempEndDate = moment()
                                        .day(15)
                                        .toDate()
                                    setStartDate(tempStartDate)
                                    setEndDate(tempEndDate)
                                    refreshData(tempStartDate, tempEndDate)
                                }}
                            >
                                Next Week
                            </Button>
                            <Button
                                value={CUSTOM}
                                aria-label="Custom"
                                style={{
                                    ...ButtonGroupStyle,
                                    backgroundColor:
                                        dateFilter === CUSTOM
                                            ? theme.palette.primary.dark
                                            : '',
                                    color: dateFilter === CUSTOM ? 'white' : '',
                                }}
                                onClick={() => setDateFilter(CUSTOM)}
                            >
                                Custom
                            </Button>
                        </ToggleButtonGroup>

                        <Container
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                                minWidth: 'fit-content',
                            }}
                        >
                            <KeyboardDatePicker
                                clearable
                                disabled={dateFilter !== CUSTOM}
                                value={startDate}
                                onChange={(date) => {
                                    setStartDate(date)
                                    if (
                                        endDate === null ||
                                        (date && endDate < date)
                                    ) {
                                        setEndDate(date)
                                    }
                                    refreshData(date, endDate)
                                }}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                style={{
                                    width: 180,
                                }}
                                label="Start Date"
                                size="small"
                            />
                            <KeyboardDatePicker
                                disabled={dateFilter !== CUSTOM}
                                error={endDate === null}
                                value={endDate}
                                onChange={(date) => {
                                    if (date === null) {
                                        return setEndDate(null)
                                    }
                                    if (startDate && date >= startDate) {
                                        refreshData(startDate, date)
                                        return setEndDate(date)
                                    }
                                    return (
                                        startDate &&
                                        toast.error(
                                            'End Date must be the day of or after the Start Date',
                                        )
                                    )
                                }}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                style={{
                                    width: 180,
                                }}
                                label="End Date"
                                size="small"
                            />
                        </Container>
                    </Container>

                    {/* Vendor Filter and User Selector Section */}
                    <Container
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                            minWidth: 'fit-content',
                        }}
                    >
                        <ToggleButtonGroup
                            value={vendorFilter}
                            exclusive
                            style={{
                                border: `1px solid ${theme.palette.primary.dark}`,
                                borderRadius: '5px',
                                height: 40,
                                minWidth: 'fit-content',
                            }}
                        >
                            <Button
                                value={ALL}
                                aria-label="All"
                                style={{
                                    ...ButtonGroupStyle,
                                    backgroundColor:
                                        vendorFilter === ALL
                                            ? theme.palette.primary.dark
                                            : '',
                                    color: vendorFilter === ALL ? 'white' : '',
                                }}
                                onClick={() => setVendorFilter(ALL)}
                            >
                                All Vendors
                            </Button>
                            <Button
                                value={IN_HOUSE}
                                aria-label="In House"
                                style={{
                                    ...ButtonGroupStyle,
                                    borderRight: `1px solid ${theme.palette.primary.dark}`,
                                    borderLeft: `1px solid ${theme.palette.primary.dark}`,
                                    backgroundColor:
                                        vendorFilter === IN_HOUSE
                                            ? theme.palette.primary.dark
                                            : '',
                                    color:
                                        vendorFilter === IN_HOUSE
                                            ? 'white'
                                            : '',
                                }}
                                onClick={() => setVendorFilter(IN_HOUSE)}
                            >
                                In House
                            </Button>
                            <Button
                                value={CONTRACTOR}
                                aria-label="Contractor"
                                style={{
                                    ...ButtonGroupStyle,
                                    backgroundColor:
                                        vendorFilter === CONTRACTOR
                                            ? theme.palette.primary.dark
                                            : '',
                                    color:
                                        vendorFilter === CONTRACTOR
                                            ? 'white'
                                            : '',
                                }}
                                onClick={() => setVendorFilter(CONTRACTOR)}
                            >
                                Contractor
                            </Button>
                            <Button
                                value={MY_TEAM}
                                aria-label="My Team"
                                style={{
                                    ...ButtonGroupStyle,
                                    borderRight: `1px solid ${theme.palette.primary.dark}`,
                                    borderLeft: `1px solid ${theme.palette.primary.dark}`,
                                    backgroundColor:
                                        vendorFilter === MY_TEAM
                                            ? theme.palette.primary.dark
                                            : '',
                                    color:
                                        vendorFilter === MY_TEAM ? 'white' : '',
                                }}
                                onClick={() => setVendorFilter(MY_TEAM)}
                            >
                                My Team
                            </Button>
                        </ToggleButtonGroup>

                        <Container
                            style={{
                                width: 250,
                                display:
                                    vendorFilter === MY_TEAM ? 'flex' : 'none',
                                alignItems: 'center',
                                minWidth: 'fit-content',
                            }}
                        >
                            <Selector
                                data={userList}
                                label="Team Members"
                                currentValue={selectedUserIds}
                                onChange={(e) => {
                                    setSelectedUserIds(
                                        e.target.value as number[],
                                    )
                                }}
                                getDisplayString={(user) => user.name}
                                searchable
                                multiple
                                size="small"
                                customStyle={{
                                    formControl: {
                                        width: '100%',
                                    },
                                }}
                                customRenderValue={(selected: unknown) => {
                                    const selectedCount = (selected as number[])
                                        .length
                                    if (selectedCount === 0)
                                        return <span>All Team Members</span>
                                    return (
                                        <span>{`${selectedCount} Team Member${
                                            selectedCount > 1 ? 's' : ''
                                        } Selected`}</span>
                                    )
                                }}
                            />
                        </Container>
                    </Container>

                    {/* Action Buttons Section */}
                    <Container
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                            flexShrink: 0,
                            minWidth: 'fit-content',
                        }}
                    >
                        <Tooltip title="Create Action Item">
                            <IconButton
                                onClick={() =>
                                    setFormState({
                                        ...formState,
                                        createWorkorderForm: true,
                                    })
                                }
                            >
                                <AddCircleOutline />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Property Analytics">
                            <IconButton
                                onClick={() =>
                                    setFormState({
                                        ...formState,
                                        propertyAnalyticsModal: true,
                                    })
                                }
                            >
                                <BarChart />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Vendor Service Rules">
                            <IconButton
                                onClick={() =>
                                    setFormState({
                                        ...formState,
                                        vendorRuleModal: true,
                                    })
                                }
                            >
                                <AccountTree />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="EZNow Requests">
                            <IconButton
                                onClick={() =>
                                    setFormState({
                                        ...formState,
                                        ezNowModal: true,
                                    })
                                }
                            >
                                <Badge
                                    badgeContent={ezNowCount}
                                    color="secondary"
                                >
                                    <EZLogo style={{ height: 30, width: 30 }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Pending Work">
                            <IconButton
                                onClick={() =>
                                    setFormState({
                                        ...formState,
                                        serviceRequestModal: true,
                                    })
                                }
                            >
                                <Badge
                                    badgeContent={
                                        failedServiceRequests +
                                        unassignedWoCount
                                    }
                                    color="secondary"
                                >
                                    <AssignmentIndOutlined />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <IconButton onClick={openFilterDrawer}>
                            <TuneIcon />
                        </IconButton>
                    </Container>
                </Container>
            </Container>
        </Container>
    )
}
